import React, { useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Icon, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';

import { Organisatie } from '../../../models/organisatie/organisatie';

import DialogStopSharingOrganisatie from '../../controls/dialogs/DialogStopSharingOrganisatie';
import DialogSharedPlansByOrganisatie from '../../controls/dialogs/DialogSharedPlansByOrganisatie';
import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';

import { planManagementService } from '../../../services/plan-management.service';
import { authService } from '../../../services/auth.service';

const SharedOrganisatiesActionMenu = ({ 
  params,
  planId,
  isZonderGebied,
  onOrganisatiesUpdate
}: {
  params: GridRenderCellParams<Organisatie>;
  planId?: string;
  isZonderGebied: boolean;
  onOrganisatiesUpdate: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpenDialogs] = useState({
    stopSharing: false,
    viewPlans: false
  });

  const warningPlanneninAfstemmingsdossier = `<br/><br/>
                <b>LET OP!</b> Als uw organisatie plannen in één of meerdere afstemmingsdossiers heeft zitten,
                zien de andere organisaties waar u plannen mee deelt uw plannen niet meer in die afstemmingsdossiers.`;

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
    handleCloseMenu();
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
    setSelectedId(null);
  };
  
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setSelectedId(id) // id: string
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleConfirmStopSharing = () => {
    if (selectedId) {
      handleStopSharing(selectedId);
    }
    closeDialog('stopSharing');
  };

  // event
  const handleStopSharing = async (organisatieId: string) => {
    if (!organisatieId) {
      console.error('Organisatie id is null');
      return;
    }

    try {
      // setLoading(true);
      let response;
      if (planId) {
        response = await planManagementService.deleteSharePlanWithOrganisation(planId, organisatieId);
      } 
      else {
        response = await planManagementService.deleteSharedOrganisatie(organisatieId);
      }

      if (response.ok) {
        // console.log('Organisatie deleted successfully:', response);
        setLoading(false);
        onOrganisatiesUpdate();
      }
    }
    catch (error) {
      setLoading(false);
      console.error('Error deleting organisatie:', error);
      setError('Er is een fout opgetreden');
    }
  }

  return (
    <>
      <IconButton onClick={(e) => handleOpenMenu(e, params.id as string)}>
        <Tooltip title="Acties" placement="right" arrow>
          <Icon>more_vert</Icon>
        </Tooltip>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => openDialog('viewPlans')}>
          <ListItemIcon>
            <Icon>share</Icon>
          </ListItemIcon>
          Bekijk plannen
        </MenuItem>
        { authService.isKeyUser() && (
          <MenuItem onClick={() => openDialog('stopSharing')}>
            <ListItemIcon>
              <Icon>delete</Icon>
            </ListItemIcon>
            Stoppen met delen
          </MenuItem>
        )}
      </Menu>

      {/* <DialogDelete
        open={open.delete}
        onClose={() => closeDialog('delete')}
        onDelete={handleConfirmDelete}
       /> */}

      <DialogSharedPlansByOrganisatie
        open={open.viewPlans}
        organisatieId={params.id as string}
        organisatieName={params.row.displayName ?? ''}
        isZonderGebied={isZonderGebied}
        onClose={() => closeDialog('viewPlans')}
      />

      <DialogStopSharingOrganisatie
        open={open.stopSharing}
        title={
          planId ? 
            "Stoppen met delen van dit plan" : 
            "Stoppen met delen van plannen"
        }
        contentText={
          planId
            ? `Weet je zeker dat je wilt stoppen met het delen van dit plan met deze organisatie?${warningPlanneninAfstemmingsdossier}`
            : `Weet je zeker dat je wilt stoppen met het delen van plannen met deze organisatie?${warningPlanneninAfstemmingsdossier}`
        }
        onClose={() => closeDialog('stopSharing')}
        onStopSharing={handleConfirmStopSharing}
       />

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default SharedOrganisatiesActionMenu;
